/**
 * Block Types — atomic content units within widget sections.
 *
 * Blocks are the building blocks of widget content: text, image, button,
 * divider, spacer, icon. Each block-enabled widget declares `defaultBlocks`
 * in its WidgetDefinition. Blocks are rendered via BlockRenderer in the
 * storefront preview, and edited via BlockInspector in the zvgnext sidebar.
 *
 * Part of Plan 145 — Block Editor Customizer.
 */

import type { AppearanceValue } from '../page-config/types';

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type BlockType =
  | 'text' | 'rich-text' | 'image' | 'button' | 'divider' | 'spacer' | 'icon'
  | 'search-bar' | 'category-nav' | 'social-link' | 'contact-item';

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK LAYOUT (position and size within parent section)
// ═══════════════════════════════════════════════════════════════════════════

export interface BlockLayout {
  /** Sort order within parent (0-based) */
  order: number;
  /** Width as percentage of parent: 25 | 33 | 50 | 75 | 100 */
  widthPct: number;
  /** Optional fixed height in pixels (undefined = auto) */
  heightPx?: number;
  /** Horizontal alignment within parent */
  align?: 'start' | 'center' | 'end';
  /** Row group within the parent widget layout (e.g., 'topbar', 'navbar', 'footer') */
  groupId?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK GROUP CONFIG (declarative group layout for multi-group widgets)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Declares a visual group within a multi-group widget (e.g., header rows, footer rows).
 * The GenericGroupedBlockLayout uses this config to render blocks per group
 * without any per-widget layout files.
 */
export interface BlockGroupConfig {
  /** Group identifier — matches BlockLayout.groupId on blocks */
  id: string;
  /** Display label for editor UI */
  label: string;
  /** CSS flex direction for the group container */
  direction: 'row' | 'column';
  /** Tailwind/CSS classes for the group's outer container */
  containerClass: string;
  /** Tailwind/CSS classes for the group's inner wrapper (e.g., max-w-7xl flex) */
  innerClass?: string;
  /** Which block types can be inserted into this group */
  allowedTypes: BlockType[];
  /** Whether blocks use natural sizing (flex: 0 0 auto) vs percentage widths */
  naturalSizing?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK CONTENT (discriminated union — type field is the discriminator)
// ═══════════════════════════════════════════════════════════════════════════

export interface TextBlockContent {
  type: 'text';
  value: string;
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
}

export interface ImageBlockContent {
  type: 'image';
  src: string;
  alt?: string;
  objectFit?: 'cover' | 'contain' | 'fill';
}

export interface ButtonBlockContent {
  type: 'button';
  label: string;
  href?: string;
  variant?: 'filled' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  /** lego step 1: own colours/radius (defaults keep the original #0ea5e9 / 8px look) */
  background?: string;
  textColor?: string;
  radius?: number;
}

export interface DividerBlockContent {
  type: 'divider';
  color?: string;
  thickness?: number;
}

export interface SpacerBlockContent {
  type: 'spacer';
  heightPx: number;
}

export interface IconBlockContent {
  type: 'icon';
  name: string;
  size?: number;
  color?: string;
  /** Runtime behavior wiring: cart badge, account link, favorites link */
  behavior?: 'cart' | 'account' | 'favorites';
}

// ═══════════════════════════════════════════════════════════════════════════
// NEW BLOCK TYPES (Plan 147 — Code Widget Decomposition)
// ═══════════════════════════════════════════════════════════════════════════

/** Platform identifiers for social link blocks */
export type SocialPlatform = 'facebook' | 'line' | 'youtube' | 'tiktok' | 'instagram';

/** Contact item types with auto-derived link behavior */
export type ContactItemType = 'phone' | 'fax' | 'email' | 'line';

export interface SearchBarBlockContent {
  type: 'search-bar';
  /** Placeholder text shown in the search input */
  placeholder?: string;
}

export interface CategoryNavBlockContent {
  type: 'category-nav';
  /** Button label (e.g., "หมวดหมู่สินค้า") */
  buttonLabel?: string;
  /** Brand color for the button background */
  brandColor?: string;
}

export interface SocialLinkBlockContent {
  type: 'social-link';
  /** Social platform — drives icon selection and brand color */
  platform: SocialPlatform;
  /** URL to the social profile page */
  url: string;
}

export interface ContactItemBlockContent {
  type: 'contact-item';
  /** Contact type — drives icon and link behavior (tel:/mailto:/line.me) */
  contactType: ContactItemType;
  /** Contact value (phone number, email address, LINE ID) */
  value: string;
  /** Optional display label override */
  label?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// RICH TEXT BLOCK (multi-item text with heading/body/caption sub-layers)
// ═══════════════════════════════════════════════════════════════════════════

/** Role of a text sub-item within a rich-text block */
export type RichTextItemRole = 'heading' | 'body' | 'caption';

/** A single text sub-item with its own styling */
export interface RichTextItem {
  /** Semantic role — determines default styling and editor label */
  role: RichTextItemRole;
  /** Text content */
  value: string;
  /** Font size in px (defaults per role: heading=28, body=16, caption=13) */
  fontSize?: number;
  /** Font weight (defaults per role: heading=700, body=400, caption=400) */
  fontWeight?: number;
  /** Text color as hex or CSS color string */
  color?: string;
  /** Horizontal text alignment */
  textAlign?: 'left' | 'center' | 'right';
}

export interface RichTextBlockContent {
  type: 'rich-text';
  /** Ordered list of text sub-items (heading, body, caption, etc.) */
  items: RichTextItem[];
}

/** Discriminated union of all block content types */
export type BlockContent =
  | TextBlockContent
  | RichTextBlockContent
  | ImageBlockContent
  | ButtonBlockContent
  | DividerBlockContent
  | SpacerBlockContent
  | IconBlockContent
  | SearchBarBlockContent
  | CategoryNavBlockContent
  | SocialLinkBlockContent
  | ContactItemBlockContent;

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK NODE (the complete block record stored in SectionEnvelope.blocks[])
// ═══════════════════════════════════════════════════════════════════════════

export interface BlockNode {
  /** Stable unique ID (nanoid for user-created, static string for defaults) */
  id: string;
  /** Block type discriminator */
  type: BlockType;
  /** Type-specific content payload */
  content: BlockContent;
  /** Position and size within parent section */
  layout: BlockLayout;
  /** Optional per-block appearance (bg, radius, padding, etc.) */
  appearance?: AppearanceValue;
}

// ═══════════════════════════════════════════════════════════════════════════
// DEFAULT BLOCK FACTORY
// ═══════════════════════════════════════════════════════════════════════════

/** Default content for each block type — used when inserting new blocks */
const DEFAULT_CONTENT: Record<BlockType, BlockContent> = {
  text: { type: 'text', value: 'New text block' },
  'rich-text': { type: 'rich-text', items: [
    { role: 'heading', value: 'Heading', fontSize: 28, fontWeight: 700 },
    { role: 'body', value: 'Body text goes here.', fontSize: 16, fontWeight: 400 },
  ] },
  image: { type: 'image', src: '', alt: '' },
  button: { type: 'button', label: 'Button' },
  divider: { type: 'divider' },
  spacer: { type: 'spacer', heightPx: 24 },
  icon: { type: 'icon', name: 'star', size: 24 },
  'search-bar': { type: 'search-bar', placeholder: 'ค้นหาสินค้า' },
  'category-nav': { type: 'category-nav', buttonLabel: 'หมวดหมู่สินค้า', brandColor: '#FF6600' },
  'social-link': { type: 'social-link', platform: 'facebook', url: '' },
  'contact-item': { type: 'contact-item', contactType: 'phone', value: '' },
};

/** Default widthPct when a block enters a standard (percentage-width) section.
 * Used by cross-section drag adaptation. Values from user story Decision 3. */
export const BLOCK_TYPE_WIDTH_DEFAULTS: Record<BlockType, number> = {
  text: 100,
  'rich-text': 100,
  image: 50,
  icon: 10,
  button: 25,
  divider: 100,
  spacer: 100,
  'search-bar': 50,
  'social-link': 10,
  'contact-item': 25,
  'category-nav': 100,
};

/**
 * Create a new BlockNode with default content for the given type.
 * @param type - The block type to create
 * @param id - Unique ID for the block (use crypto.randomUUID() or nanoid)
 */
export function createDefaultBlock(type: BlockType, id: string): BlockNode {
  return {
    id,
    type,
    content: { ...DEFAULT_CONTENT[type] },
    layout: { order: 0, widthPct: 100 },
  };
}
